import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { ChevronDown, ChevronUp, X, AlertCircle } from "lucide-react";
import { Drawer } from "@/components/organisms/Drawer";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { Tooltip } from "@/components/atoms/Tooltip";
import { DiaryDateField, DiaryDateTimeField, DiaryTimeField } from "@/modules/diary/DiaryDateTimeField";
import { WeekdayPicker } from "./WeekdayPicker";
import { usersService } from "@/services/usersService";
import { availabilityService } from "@/services/availabilityService";
import { useSettingsStore } from "@/store/settingsStore";
import type { UserSummary } from "@/types/entities";
import {
  DEFAULT_SLOT_SETTINGS,
  resolveSlotSettings,
  type AvailabilityCreate,
  type RecurrenceFreq,
  type SlotSettings,
} from "@/types/availability";
import { lastRecurrenceOccurrenceDateKey } from "@/modules/diary/diaryEntryUtils";
import { buildUserSelectOptions, userIdentityKey } from "./searchHelpers";
import { defaultDiarySlotForDate, fromDatetimeLocalToApi } from "@/utils/date-helpers";
import { getApiErrorMessage } from "@/utils/apiErrors";

export interface AvailabilityProperty {
  id: string;
  sourceCrm: string | null;
  externalId: string | null;
  displayAddress?: string | null;
  legalEntityId?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * The properties this window is created for. One availability entry is created per
   * property — the same call the single-property flow makes, repeated — so each listing
   * keeps its own entry to edit, delete or reset independently.
   */
  properties: AvailabilityProperty[];
  /** Seed date for the window (defaults to now). */
  seedDate?: Date;
  /** Called after at least one entry was created, with the outcome of the whole batch. */
  onCreated?: (summary: { created: number; failed: number }) => void;
}

/** Runs `fn` over `items`, at most `limit` in flight, keeping the input order. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
  onSettled?: () => void,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (let i = cursor++; i < items.length; i = cursor++) {
      try {
        results[i] = { status: "fulfilled", value: await fn(items[i]) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
      onSettled?.();
    }
  });
  await Promise.all(workers);
  return results;
}

// Assigned-users picker hidden for now — flip to true to re-enable. The priority
// list is still seeded from the property's default users and sent on create.
const SHOW_ASSIGNED_USERS = false;

const RECURRENCE_OPTIONS = [
  { value: "NONE", label: "Does not repeat" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
];

function datePart(value: string): string {
  return value.split("T")[0] ?? "";
}

function timePart(value: string): string {
  return value.split("T")[1] ?? "";
}

/** `YYYY-MM-DD` → `DD/MM/YYYY`, matching the Start field's date format. */
function formatDateDMY(dateKey: string): string {
  const [y, m, d] = dateKey.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
}

function withDate(value: string, date: string): string {
  const time = timePart(value);
  if (!date || !time) return value;
  return `${date}T${time}`;
}

export function AvailabilityDrawer({ open, onClose, properties, seedDate, onCreated }: Props) {
  const legalEntityIdFromStore = useSettingsStore((s) => s.legalEntityId);
  // One property is the common case; several is the bulk flow from the properties list.
  const isBulk = properties.length > 1;
  const firstProperty = properties[0] as AvailabilityProperty | undefined;
  // Users and the (single-property) settings lookup are scoped to the legal entity
  // the properties belong to, falling back to the app context as before.
  const legalEntityId = firstProperty?.legalEntityId ?? legalEntityIdFromStore ?? "";
  // Identity of the current selection — the effect below reloads on it rather than on the
  // array reference, which the parent rebuilds on every render.
  const propertyIdsKey = properties.map((p) => p.id).join(",");

  const initial = useMemo(() => defaultDiarySlotForDate(seedDate ?? new Date()), [seedDate]);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [recurrence, setRecurrence] = useState<RecurrenceFreq>("NONE");
  const [interval, setInterval] = useState("1");
  const [until, setUntil] = useState("");
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>([]);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [exDraft, setExDraft] = useState("");
  const [assignedUsers, setAssignedUsers] = useState<UserSummary[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserSummary[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [settings, setSettings] = useState<SlotSettings>(DEFAULT_SLOT_SETTINGS);
  /** Effective settings per property — `null` where the property resolves to no scope at all. */
  const [settingsByPropertyId, setSettingsByPropertyId] = useState<Record<string, SlotSettings | null>>({});
  /** Each property's default users, seeded from its own effective settings. */
  const [seededUsersByPropertyId, setSeededUsersByPropertyId] = useState<Record<string, UserSummary[]>>({});
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!open || properties.length === 0) {
      setSubmitAttempted(false);
      return;
    }
    setSubmitAttempted(false);
    setStart(initial.start);
    setEnd(initial.end);
    setRecurrence("NONE");
    setInterval("1");
    setUntil("");
    setDaysOfWeek([]);
    setExcluded([]);
    setExDraft("");
    setAssignedUsers([]);
    setUserSearchQuery("");
    setSavedCount(0);
    setLoadingUsers(true);
    const usersP = usersService
      .list({ legalEntityId: legalEntityId || undefined }, { page: 0, size: 200 })
      // Only show CRM-linked users — drop any with a null/empty sourceCrm.
      .then((res) => (res.content ?? []).filter((u) => u.sourceCrm))
      .catch((error) => {
        toast.error(
          getApiErrorMessage(error) ?? "Failed to load users for availability.",
          { toastId: "availability-users-load-error" },
        );
        return [] as UserSummary[];
      });
    // Read-only: the settings that already apply to each property, wherever they are
    // configured. Resolved server-side from the property's own branch and legal entity, so
    // branch- and LE-level settings are found without the drawer having to guess the scope.
    // `null` means the property resolves to nothing anywhere — the warning case.
    let settingsLoadError: unknown = null;
    const settingsP = mapWithConcurrency(properties, 5, (p) =>
      availabilityService
        .getEffectiveSettings(p.id)
        .then((s) => resolveSlotSettings(s))
        .catch((error) => {
          // A missing effective configuration is a valid "no settings" state.
          // Network/server failures are different and must be visible to the user.
          if ((error as { status?: number } | null)?.status !== 404 && settingsLoadError === null) {
            settingsLoadError = error;
          }
          return null;
        }),
    ).then((results) =>
      results.map((r) => (r.status === "fulfilled" ? r.value : null)),
    );
    Promise.all([usersP, settingsP])
      .then(([users, resolvedList]) => {
        if (settingsLoadError != null) {
          toast.error(
            getApiErrorMessage(settingsLoadError) ?? "Failed to load availability settings.",
            { toastId: "availability-settings-load-error" },
          );
        }
        setAvailableUsers(users);
        const byProperty: Record<string, SlotSettings | null> = {};
        const seedsByProperty: Record<string, UserSummary[]> = {};
        properties.forEach((p, index) => {
          const resolved = resolvedList[index];
          byProperty[p.id] = resolved;
          // Each property's own default users (if any), in priority order.
          seedsByProperty[p.id] = (resolved?.users ?? [])
            .map((ref) => users.find((u) => u.id === ref.id))
            .filter((u): u is UserSummary => Boolean(u));
        });
        setSettingsByPropertyId(byProperty);
        setSeededUsersByPropertyId(seedsByProperty);
        setSettings(resolvedList[0] ?? DEFAULT_SLOT_SETTINGS);
        // Seed the priority list with the first property's default users, preserving their
        // order. The user can still remove them, add others, or reorder. In the bulk flow
        // each property sends its own seeds instead (see `usersForProperty`).
        setAssignedUsers(seedsByProperty[properties[0].id] ?? []);
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial.start, initial.end, legalEntityId, propertyIdsKey]);

  const userOptions = useMemo(
    () =>
      buildUserSelectOptions(availableUsers, {
        searchQuery: userSearchQuery,
        excludeIdentityKeys: assignedUsers.map(userIdentityKey),
      }),
    [availableUsers, assignedUsers, userSearchQuery],
  );

  function addUser(id: string) {
    const found = availableUsers.find((user) => user.id === id);
    if (found) setAssignedUsers((prev) => [...prev, found]);
  }
  function removeUser(id: string) {
    setAssignedUsers((prev) => prev.filter((u) => u.id !== id));
  }
  function addExcluded() {
    if (!exDraft) return;
    setExcluded((prev) => (prev.includes(exDraft) ? prev : [...prev, exDraft].sort()));
    setExDraft("");
  }
  function removeExcluded(d: string) {
    setExcluded((prev) => prev.filter((x) => x !== d));
  }
  function move(idx: number, delta: number) {
    setAssignedUsers((prev) => {
      const next = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }

  function handleStartChange(nextStart: string) {
    const prevDate = datePart(start);
    const nextDate = datePart(nextStart);
    setStart(nextStart);
    setEnd((currentEnd) => {
      const updated = withDate(currentEnd, nextDate);
      if (updated <= nextStart) return nextStart;
      return updated;
    });
    // Any change to the start day clears a previously picked repeat-until and
    // any skip-days selection (both are bounded to the start date), so the user
    // re-picks them against the new start.
    if (nextDate !== prevDate) {
      setUntil("");
      setExcluded([]);
      setExDraft("");
    }
  }

  function handleEndChange(nextEnd: string) {
    // The end date is locked to the start day — keep only the picked time.
    const candidate = `${datePart(start)}T${timePart(nextEnd)}`;
    if (candidate <= start) {
      toast.error("End time must be after start time.");
      return;
    }
    setEnd(candidate);
  }

  /**
   * Who takes viewings in the created window. A single property uses the (editable)
   * priority list; a bulk create gives every property the users its own settings
   * name, so the result is the same as creating each one on its own.
   */
  function usersForProperty(target: AvailabilityProperty): UserSummary[] {
    if (!isBulk) return assignedUsers;
    return seededUsersByPropertyId[target.id] ?? [];
  }

  function buildPayload(
    target: AvailabilityProperty,
    startApi: string,
    endApi: string,
  ): AvailabilityCreate {
    const targetSourceCrm = target.sourceCrm ?? "ALTO";
    return {
      sourceCrm: targetSourceCrm,
      legalEntityId: target.legalEntityId ?? legalEntityIdFromStore ?? "",
      header: null,
      bridgeBranchId: null,
      instanceId: crypto.randomUUID(),
      type: "GENERAL",
      entryType: "AVAILABILITY",
      title: `Availability: ${target.displayAddress ?? target.id}`,
      description: null,
      startDate: startApi,
      endDate: endApi,
      status: "ACTIVE",
      isCancelled: false,
      recurring: recurrence !== "NONE",
      recurrenceType: recurrence === "NONE" ? null : recurrence,
      recurrenceInterval: recurrence === "NONE" ? null : Math.max(1, Number(interval) || 1),
      recurrenceUntil: recurrence === "NONE" || !until ? null : `${until}T23:59:59`,
      recurrenceDaysOfWeek: recurrence === "WEEKLY" && daysOfWeek.length ? daysOfWeek : null,
      recurrenceExceptions: recurrence === "NONE" || excluded.length === 0 ? null : excluded,
      users: usersForProperty(target).map((u) => ({
        id: u.id,
        sourceCrm: u.sourceCrm,
        externalId: u.externalId,
      })),
      properties: [
        { id: target.id, sourceCrm: targetSourceCrm, externalId: target.externalId ?? "" },
      ],
    };
  }

  async function submit() {
    setSubmitAttempted(true);

    const startApi = fromDatetimeLocalToApi(start);
    const endApi = fromDatetimeLocalToApi(end);
    if (!startApi || !endApi) {
      return;
    }
    const now = new Date();
    if (new Date(startApi) < now) {
      return;
    }
    if (new Date(endApi) <= new Date(startApi)) {
      return;
    }
    if (recurrence !== "NONE" && (!until.trim() || until < datePart(start))) {
      return;
    }
    if (properties.length === 0) {
      toast.error("No property selected.");
      return;
    }
    // Every entry needs a legal entity: the property's own, or the app context.
    const withoutLegalEntity = properties.filter(
      (p) => !(p.legalEntityId ?? legalEntityIdFromStore),
    );
    if (withoutLegalEntity.length > 0) {
      toast.error("No legal entity selected.");
      return;
    }
    // Removed assignedUsers length check to allow creating availability without default users.
    try {
      setSaving(true);
      setSavedCount(0);
      // Only the availability is created. This used to write a property-scoped slot_settings row
      // first, which silently copied branch/legal-entity values down onto the property — the
      // property then stopped inheriting, kept a stale snapshot of rules it never chose, and there
      // was no way to tell which scope the values had come from. Slicing reads the cascade at
      // projection time instead.
      //
      // One request per property rather than one entry naming them all: each property keeps its
      // own entry, so it can be edited, deleted or reset without touching the others.
      const results = await mapWithConcurrency(
        properties,
        4,
        (p) => availabilityService.create(buildPayload(p, startApi, endApi)),
        () => setSavedCount((n) => n + 1),
      );
      const failures = results
        .map((r, index) => ({ r, property: properties[index] }))
        .filter(({ r }) => r.status === "rejected");
      const created = results.length - failures.length;

      if (failures.length === 0) {
        toast.success(
          created === 1 ? "Availability created." : `Availability created for ${created} properties.`,
        );
        onCreated?.({ created, failed: 0 });
        onClose();
        return;
      }
      // Partial success has to stay on screen: the window is still in the form, so the
      // operator can retry the ones that failed without re-entering anything.
      if (created > 0) {
        toast.warning(
          `Availability created for ${created} of ${results.length} properties. ` +
            `Failed: ${failures
              .slice(0, 3)
              .map(({ property: p }) => p.displayAddress ?? p.id)
              .join(", ")}${failures.length > 3 ? ` and ${failures.length - 3} more` : ""}.`,
        );
        onCreated?.({ created, failed: failures.length });
        return;
      }
      showActionErrorFromUnknown(
        (failures[0].r as PromiseRejectedResult).reason,
        "Failed to create availability.",
      );
    } catch (e) {
      showActionErrorFromUnknown(e, "Failed to create availability.");
    } finally {
      setSaving(false);
    }
  }

  const startApiValue = fromDatetimeLocalToApi(start);
  const endApiValue = fromDatetimeLocalToApi(end);
  const now = new Date();
  const showValidation = submitAttempted;
  const startDateKey = datePart(start);
  const recurUntilInvalid = recurrence !== "NONE" && Boolean(until) && until < startDateKey;
  const dailyLastOccurrence = useMemo(() => {
    if (recurrence !== "DAILY" || !until.trim() || recurUntilInvalid || !startApiValue) return null;
    return lastRecurrenceOccurrenceDateKey({
      startDate: startApiValue,
      recurring: true,
      recurrenceType: "DAILY",
      recurrenceInterval: Math.max(1, Number(interval) || 1),
      recurrenceUntil: `${until}T23:59:59`,
      recurrenceExceptions: excluded.length ? excluded : null,
      recurrenceDaysOfWeek: null,
    });
  }, [recurrence, until, recurUntilInvalid, startApiValue, interval, excluded]);

  /**
   * Where the slot rules for this availability come from. They are never edited here and never
   * copied onto the property — this only says which scope the booking engine will read at
   * projection time, so an empty result is a warning rather than something to silently fill in.
   */
  const slotRulesNotice = useMemo(() => {
    if (isBulk) {
      // One line for the whole selection: which properties can actually generate slots.
      // Naming every scope would be a wall of text, so only the gap is called out.
      const withoutSettings = properties.filter((p) => !settingsByPropertyId[p.id]?.resolvedScope);
      if (withoutSettings.length === properties.length) {
        return (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            None of the {properties.length} selected properties have viewing settings configured at
            property, branch or legal-entity level. The availability will be saved but no bookable
            slots can be generated until settings are set up.
          </p>
        );
      }
      if (withoutSettings.length > 0) {
        return (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {withoutSettings.length} of {properties.length} selected properties have no viewing
            settings configured, so their availability will not generate bookable slots yet.
          </p>
        );
      }
      return (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Slot rules are inherited per property from its own property, branch or legal-entity
          settings — this window is created against all {properties.length} of them.
        </p>
      );
    }
    const scope = settings.resolvedScope;
    if (!scope) {
      return (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          No viewing settings are configured for this property, its branch or its legal entity.
          The availability will be saved but no bookable slots can be generated until settings are
          set up at one of those levels.
        </p>
      );
    }
    const label =
      scope === "PROPERTY" ? "this property" : scope === "BRANCH" ? "its branch" : "its legal entity";
    // A zero interval is back-to-back viewings, not "every 0 minutes".
    const cadence =
      settings.slotInterval == null
        ? "every ? min"
        : settings.slotInterval === 0
          ? "back-to-back"
          : `every ${settings.slotInterval} min`;
    return (
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Slot rules ({settings.slotDuration ?? "?"} min, {cadence}) are inherited from{" "}
        <span className="font-medium">{label}</span>.
      </p>
    );
  }, [settings, isBulk, properties, settingsByPropertyId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add availability"
      subtitle={
        isBulk
          ? `${properties.length} properties selected`
          : (firstProperty?.displayAddress ?? firstProperty?.id)
      }
      width="md"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={saving}>
            {saving && isBulk
              ? `Creating ${savedCount}/${properties.length}…`
              : isBulk
                ? `Create for ${properties.length} properties`
                : "Create"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {slotRulesNotice}

        {/* The selection was made on another screen, so it is restated here: the
            operator is about to write the same window to every one of them. */}
        {isBulk && (
          <div className="rounded-md border border-bridge-border/60 bg-bridge-surface/60 p-3">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-bridge-muted">
                Applies to {properties.length} properties
              </span>
            </div>
            <ul className="max-h-32 space-y-0.5 overflow-auto pr-1 text-sm text-bridge-fg">
              {properties.map((p) => (
                <li key={p.id} className="flex items-center gap-2 truncate">
                  {!settingsByPropertyId[p.id]?.resolvedScope && !loadingUsers && (
                    <Tooltip content="No viewing settings configured — no bookable slots yet" side="top">
                      <AlertCircle size={12} className="shrink-0 text-amber-500" />
                    </Tooltip>
                  )}
                  <span className="truncate">{p.displayAddress ?? p.id}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Tooltip
              side="top"
              hoverOnly
              content="When this viewing window opens. Must be in the future — the date here also sets the end date."
            >
              <div>
                <DiaryDateTimeField id="avail-start" label="Start" value={start} onChange={handleStartChange} min={new Date().toISOString().slice(0, 16)} />
              </div>
            </Tooltip>
            <FieldValidationError
              show={showValidation && !startApiValue}
              message="Please set a valid start time."
            />
            <FieldValidationError
              show={showValidation && Boolean(startApiValue) && new Date(startApiValue!) < now}
              message="Start date must be in the future."
            />
          </div>
          <div className="w-full min-w-0">
            <Tooltip
              side="top"
              hoverOnly
              content="When the window closes. A window can't span days, so the date is locked to the start date and only the time is editable."
            >
              <div>
                <label htmlFor="avail-end" className="text-xs font-medium text-bridge-muted block mb-1">End</label>
                <div className="flex gap-2">
                  <div
                    className="h-10 flex-1 min-w-0 px-3 flex items-center rounded-md border border-bridge-border/70 bg-bridge-surface/70 text-sm text-bridge-fg opacity-60 select-none"
                    aria-label="End date (locked to start date)"
                  >
                    {formatDateDMY(datePart(start))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <DiaryTimeField id="avail-end" value={end} onChange={handleEndChange} min={start} />
                  </div>
                </div>
              </div>
            </Tooltip>
            <FieldValidationError
              show={showValidation && !endApiValue}
              message="Please set a valid end time."
            />
            <FieldValidationError
              show={
                showValidation &&
                Boolean(startApiValue) &&
                Boolean(endApiValue) &&
                new Date(endApiValue!) <= new Date(startApiValue!)
              }
              message="End time must be after start time."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Tooltip
            side="top"
            hoverOnly
            content="Create this window once, or repeat it daily/weekly until a chosen end date."
          >
            <div>
              <MenuSelect
                label="Repeat"
                value={recurrence}
                onChange={(v) => setRecurrence(v as RecurrenceFreq)}
                options={RECURRENCE_OPTIONS}
              />
            </div>
          </Tooltip>
          {recurrence !== "NONE" && (
            <div>
              <Tooltip
                side="top"
                hoverOnly
                content={
                  recurrence === "WEEKLY"
                    ? "Gap between repeats in weeks — 1 is every week, 2 every other week."
                    : "Gap between repeats in days — 1 is every day, 2 every other day."
                }
              >
                <label className="text-xs font-medium text-bridge-muted block mb-1">Repeat Every</label>
              </Tooltip>
              <Input
                type="text"
                inputMode="numeric"
                value={interval}
                onChange={(e) => setInterval(e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, ""))}
                onKeyDown={(e) => {
                  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                  e.preventDefault();
                  const next = Math.max(1, (Number(interval) || 0) + (e.key === "ArrowUp" ? 1 : -1));
                  setInterval(String(next));
                }}
              />
              {recurrence === "DAILY" && (
                <p className="mt-1 text-[11px] text-bridge-subtle">
                  Repeats every {Math.max(1, Number(interval) || 1)} day
                  {(Number(interval) || 1) === 1 ? "" : "s"} from the start date.
                </p>
              )}
            </div>
          )}
        </div>

        {recurrence === "WEEKLY" && (
          <div>
            <Tooltip
              side="top"
              hoverOnly
              content="Weekdays the window repeats on. Leave all unselected to follow the start date's weekday."
            >
              <div>
                <label className="text-xs font-medium text-bridge-muted block mb-1">Repeat on</label>
                <WeekdayPicker value={daysOfWeek} onChange={setDaysOfWeek} />
              </div>
            </Tooltip>
            <p className="mt-1 text-[11px] text-bridge-subtle">
              Pick days (e.g. Mon–Fri to skip weekends). Empty = the start day's weekday.
            </p>
          </div>
        )}

        {recurrence !== "NONE" && (
          <div>
            <Tooltip
              side="top"
              hoverOnly
              content="Last date the repeat can run to. Must be on or after the start date, and is required once repeat is on."
            >
              <div>
                <label htmlFor="avail-until" className="text-xs font-medium text-bridge-muted block mb-1">
                  Repeat until <span className="text-red-500">*</span>
                </label>
                <DiaryDateField
                  id="avail-until"
                  value={until}
                  onChange={setUntil}
                  placeholder="Select date"
                  min={startDateKey || new Date().toISOString().slice(0, 10)}
                />
              </div>
            </Tooltip>
            <FieldValidationError
              show={showValidation && !until.trim()}
              message="Repeat until is required when repeat is enabled."
            />
            <FieldValidationError
              show={showValidation && recurUntilInvalid}
              message="Repeat until must be on or after the start date."
            />
            {recurrence === "DAILY" && dailyLastOccurrence && (
              <p className="mt-1 text-[11px] text-bridge-subtle">
                Last occurrence: {dailyLastOccurrence}
                {dailyLastOccurrence !== until && " (before the repeat-until date)"}
              </p>
            )}
          </div>
        )}

        {recurrence !== "NONE" && (
          <div>
            <Tooltip
              side="top"
              hoverOnly
              content="Dates the repeat should skip — bank holidays, days off. Pick a date and press Add."
            >
              <label className="text-xs font-medium text-bridge-muted block mb-1">
                Skip days (exclude these dates)
              </label>
            </Tooltip>
            <div className="flex items-end gap-2">
              <div className="flex-1 min-w-0">
                <DiaryDateField value={exDraft} onChange={setExDraft} placeholder="Select date" min={startDateKey || new Date().toISOString().slice(0, 10)} />
              </div>
              <Button variant="primary" size="sm" onClick={addExcluded} disabled={!exDraft}>
                Add
              </Button>
            </div>
            {excluded.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2">
                {excluded.map((d) => (
                  <li
                    key={d}
                    className="flex items-center gap-1 rounded-full border border-bridge-border/60 bg-bridge-surface px-2 py-0.5 text-xs"
                  >
                    {d}
                    <button type="button" className="text-bridge-muted hover:text-rose-500"
                            onClick={() => removeExcluded(d)} aria-label={`Remove ${d}`}>
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {SHOW_ASSIGNED_USERS && (
        <div>
          <Tooltip
            side="top"
            hoverOnly
            content="Who can take viewings in this window. The order is the priority the first available user is picked in — use the arrows to reorder."
          >
            <div>
              <label className="text-xs font-medium text-bridge-muted block mb-1">
                Assigned users (priority order) <span className="text-red-500">*</span>
              </label>
              <MenuSelect
                value=""
                placeholder={loadingUsers ? "Loading users…" : "Add a user…"}
                searchable
                loading={loadingUsers}
                onChange={addUser}
                onDropdownSearchQueryChange={setUserSearchQuery}
                options={userOptions}
              />
            </div>
          </Tooltip>
          <ol className="mt-2 space-y-1">
            {assignedUsers.map((u, idx) => (
              <li
                key={u.id}
                className="flex items-center gap-2 rounded-md border border-bridge-border/60 px-2 py-1 text-sm"
              >
                <span className="w-5 text-center text-xs font-semibold text-bridge-muted">{idx + 1}</span>
                <span className="flex-1 truncate">{u.name || u.email || u.id}</span>
                <button type="button" className="text-bridge-muted hover:text-bridge-fg disabled:opacity-30"
                        onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="Move up">
                  <ChevronUp size={16} />
                </button>
                <button type="button" className="text-bridge-muted hover:text-bridge-fg disabled:opacity-30"
                        onClick={() => move(idx, 1)} disabled={idx === assignedUsers.length - 1} aria-label="Move down">
                  <ChevronDown size={16} />
                </button>
                <button type="button" className="text-bridge-muted hover:text-rose-500"
                        onClick={() => removeUser(u.id)} aria-label="Remove">
                  <X size={16} />
                </button>
              </li>
            ))}
            {assignedUsers.length === 0 && (
              <li className="text-xs text-bridge-subtle px-1 py-1">No users added yet.</li>
            )}
          </ol>
          <FieldValidationError
            show={showValidation && assignedUsers.length === 0}
            message="Add at least one user (priority list)."
          />
        </div>
        )}
      </div>
    </Drawer>
  );
}

function FieldValidationError({
  show,
  message,
}: {
  show: boolean;
  message: string;
}) {
  if (!show) return null;
  return (
    <div className="mt-1 flex animate-in items-center gap-1.5 text-xs font-medium text-red-500 fade-in slide-in-from-top-1 duration-200">
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}
